import React, { useState, useEffect, useRef } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Button,
   FormControl,
   FormLabel,
   Input,
   VStack,
   useColorMode,
   HStack,
   Text,
   Stack,
   Flex,
   Box,
   IconButton,
   useColorModeValue,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { getDaybook, getDaybookForPayments } from '../../../redux/actions/daybook';
import { getAccount } from '../../../redux/actions/account';
import { useUser } from '../../../contexts';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';
import { DAYBOOK_TYPE } from 'constants';
import AddEditForm from '../masters/Pages/AllAccountMaster/AddEditFormForAccount';
import MultiSelectDropdown from 'components/ui/MultiSelectDropdown';

const CashBankFilterModal = ({ isOpen, onClose, onApplyFilters, initialFilters, isBankPageTrue }) => {
   const dispatch = useDispatch();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const { user } = useUser();
   const companyId = user?.loginhistory?.companyId;
   const isColorMode = useColorModeValue(true, false);
   const daybooks = useSelector((state) => state.daybook?.list || []);
   const accounts = useSelector((state) => state.account?.list || []);
   const Totals = useSelector((state) => state.voucher.pagination.totals || []);
   const [paymentStatus, setPaymentStatus] = useState('all');
   const [errors, setErrors] = useState({});
   const [currentFocusIndex, setCurrentFocusIndex] = useState(0);

   // Refs for focusable elements
   const receiptBtnRef = useRef(null);
   const paymentBtnRef = useRef(null);
   const fromDateRef = useRef(null);
   const toDateRef = useRef(null);
   const docNumberRef = useRef(null);
   const daybookRef = useRef(null);
   const accountRef = useRef(null);
   const resetBtnRef = useRef(null);
   const applyBtnRef = useRef(null);

   // Build focusable elements array
   const focusableElements = [
      receiptBtnRef,
      paymentBtnRef,
      fromDateRef,
      toDateRef,
      docNumberRef,
      daybookRef,
      accountRef,
      resetBtnRef,
      applyBtnRef,
   ];

   // Initialize filters with default values or existing values
   const [filters, setFilters] = useState({
      paymentStatus: initialFilters?.paymentStatus || 'all',
      invoiceBook: initialFilters?.invoiceBook || 'all',
      duration: initialFilters?.duration || 'any',
      contact: initialFilters?.contact || '',
      item: initialFilters?.item || '',
      daybookId: initialFilters?.daybookId || '',
      accountId: initialFilters?.accountId || '',
      fromDate: initialFilters?.fromDate || '',
      toDate: initialFilters?.toDate || '',
      docNumber: initialFilters?.docNumber || '',
      txnType: initialFilters?.txnType || '',
   });

   // Fetch daybooks and accounts when modal opens
   useEffect(() => {
      if (isOpen && companyId) {
         console.log('user', user);
         // dispatch(
         //    getDaybook({
         //       type: isBankPageTrue ? DAYBOOK_TYPE.BANK : DAYBOOK_TYPE.CASH,
         //       companyId: user?.loginhistory?.companyId,
         //    }),
         // );
         dispatch(getAccount({ compId: user?.loginhistory?.companyId, status: true, }));
      }
   }, [isOpen, dispatch, companyId, isBankPageTrue]);

   // Set initial focus when modal opens
   useEffect(() => {
      if (isOpen) {
         setTimeout(() => {
            setCurrentFocusIndex(0);
            focusableElements[0]?.current?.focus();
         }, 100);
      }
   }, [isOpen]);

   // Handle ESC key to close modal
   useEffect(() => {
      const handleKeyDown = (e) => {
         if (e.key === 'Escape') {
            onClose();
         }
      };

      if (isOpen) {
         window.addEventListener('keydown', handleKeyDown, true);
         return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
         };
      }
   }, [onClose, isOpen]);

   // Handle keyboard navigation
   const handleKeyDown = (e) => {
      if (e.key === 'Tab' || e.key === 'Enter') {
         e.preventDefault();
         const nextIndex = (currentFocusIndex + 1) % focusableElements.length;
         setCurrentFocusIndex(nextIndex);
         focusableElements[nextIndex]?.current?.focus();
      }
   };

   // Add event listener for keyboard navigation
   useEffect(() => {
      if (isOpen) {
         document.addEventListener('keydown', handleKeyDown);
         return () => {
            document.removeEventListener('keydown', handleKeyDown);
         };
      }
   }, [isOpen, currentFocusIndex]);

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
   };

   const handleInputChange = (section, field, value) => {
      setFilters((prev) => ({
         ...prev,
         [field]: value,
      }));
   };

   const handleApply = () => {
      onApplyFilters(filters);
      onClose();
   };

   const handleClear = () => {
      const clearedFilters = {
         paymentStatus: 'all',
         invoiceBook: 'all',
         duration: 'any',
         contact: '',
         item: '',
         daybookId: '',
         accountId: '',
         fromDate: '',
         toDate: '',
         docNumber: '',
         txnType: '',
      };
      setFilters(clearedFilters);
      setPaymentStatus('all');
      onApplyFilters(clearedFilters);
   };

   return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnEsc={true} isCentered>
         <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
         <ModalContent
            className={`${isDark ? 'bg-gray-800' : 'bg-white'} shadow-2xl rounded-2xl border ${isDark ? 'border-gray-700' : 'border-gray-100'
               }`}>
            {/* Header */}
            <ModalHeader
               className={`${isDark
                  ? 'bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 border-gray-700'
                  : 'bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-gray-200'
                  } border-b rounded-t-2xl`}>
               <Flex alignItems="center" gap={3}>
                  <div
                     className={`w-10 h-10 rounded-xl ${isDark ? 'bg-blue-600' : 'bg-blue-500'
                        } flex items-center justify-center shadow-lg`}>
                     <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                        />
                     </svg>
                  </div>
                  <Text className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                     Advanced Filters
                  </Text>
               </Flex>
               <ModalCloseButton
                  className={`${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
               />
            </ModalHeader>

            <ModalBody py={6} px={6}>
               <VStack spacing={3} align="stretch">
                  {/* Payment Status Section */}
                  <Box
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <HStack mb={3} spacing={2}>
                        <Box className="w-1 h-5 bg-blue-500 rounded-full" />
                        <Text
                           fontSize="sm"
                           fontWeight="700"
                           className={`${isDark ? 'text-gray-200' : 'text-gray-700'} uppercase tracking-wide`}>
                           Payment Status
                        </Text>
                     </HStack>
                     <Flex flexWrap="wrap" gap={2}>
                        <Button
                           ref={receiptBtnRef}
                           size="sm"
                           className={`rounded-full font-medium transition-all duration-200 ${paymentStatus === 'pending'
                              ? isDark
                                 ? 'bg-blue-600 text-white hover:bg-blue-700'
                                 : 'bg-blue-500 text-white hover:bg-blue-600'
                              : isDark
                                 ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                                 : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                              }`}
                           onClick={() => {
                              setPaymentStatus('pending');
                              setFilters((prev) => ({ ...prev, txnType: 0 }));
                           }}
                           onKeyDown={handleKeyDown}
                           onFocus={() => setCurrentFocusIndex(0)}
                           px={4}
                           h="36px">
                           Receive{' '}
                           <Box as="span" className="ml-2 font-bold">
                              {Totals.totalReceipts || 63}
                           </Box>
                        </Button>
                        <Button
                           ref={paymentBtnRef}
                           size="sm"
                           className={`rounded-full font-medium transition-all duration-200 ${paymentStatus === 'received'
                              ? isDark
                                 ? 'bg-blue-600 text-white hover:bg-blue-700'
                                 : 'bg-blue-500 text-white hover:bg-blue-600'
                              : isDark
                                 ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                                 : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                              }`}
                           onClick={() => {
                              setPaymentStatus('received');
                              setFilters((prev) => ({ ...prev, txnType: 1 }));
                           }}
                           onFocus={() => setCurrentFocusIndex(1)}
                           px={4}
                           h="36px">
                           Payment{' '}
                           <Box as="span" className="ml-2 font-bold">
                              {Totals.totalPayments || 32}
                           </Box>
                        </Button>
                     </Flex>
                  </Box>

                  {/* Date Range Section */}
                  <Box
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <HStack mb={3} spacing={2}>
                        <Box className="w-1 h-5 bg-green-500 rounded-full" />
                        <Text
                           fontSize="sm"
                           fontWeight="700"
                           className={`${isDark ? 'text-gray-200' : 'text-gray-700'} uppercase tracking-wide`}>
                           Date Range
                        </Text>
                     </HStack>
                     <Flex gap={4} direction={{ base: 'column', sm: 'row' }}>
                        <FormControl flex="1">
                           <FormLabel
                              fontSize="xs"
                              className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2 font-medium`}>
                              From Date
                           </FormLabel>
                           <Input
                              ref={fromDateRef}
                              type="date"
                              name="fromDate"
                              value={filters.fromDate}
                              onChange={handleChange}
                              className={`${isDark
                                 ? 'bg-gray-700 border-gray-600 text-gray-200'
                                 : 'bg-gray-50 border-gray-300 text-gray-700'
                                 } rounded-lg transition-all duration-200`}
                              focusBorderColor="blue.500"
                              onFocus={() => setCurrentFocusIndex(2)}
                              size="md"
                              h="44px"
                           />
                        </FormControl>
                        <FormControl flex="1">
                           <FormLabel
                              fontSize="xs"
                              className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2 font-medium`}>
                              To Date
                           </FormLabel>
                           <Input
                              ref={toDateRef}
                              type="date"
                              name="toDate"
                              value={filters.toDate}
                              onChange={handleChange}
                              className={`${isDark
                                 ? 'bg-gray-700 border-gray-600 text-gray-200'
                                 : 'bg-gray-50 border-gray-300 text-gray-700'
                                 } rounded-lg transition-all duration-200`}
                              focusBorderColor="blue.500"
                              onFocus={() => setCurrentFocusIndex(3)}
                              size="md"
                              h="44px"
                           />
                        </FormControl>
                     </Flex>
                  </Box>

                  {/* Doc No Section */}
                  <Box
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <FormControl>
                        <FormLabel
                           className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'
                              } mb-2 flex items-center gap-2`}>
                           <span
                              className={`w-1 h-4 rounded-full ${isDark ? 'bg-yellow-500' : 'bg-yellow-600'}`}></span>
                           Doc No
                        </FormLabel>
                        <Input
                           name='docNumber'
                           ref={docNumberRef}
                           placeholder="Enter document number"
                           value={filters.docNumber}
                           onChange={handleChange}
                           className={`${isDark
                              ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500'
                              : 'bg-gray-50 border-gray-300 text-gray-700 placeholder-gray-400'
                              } rounded-lg transition-all duration-200`}
                           focusBorderColor="blue.500"
                           size="md"
                           h="44px"
                        />
                     </FormControl>
                  </Box>

                  {/* Daybook Section */}
                  <Box
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <HStack mb={3} spacing={2}>
                        <Box className="w-1 h-5 bg-blue-500 rounded-full" />
                        <Text
                           fontSize="sm"
                           fontWeight="700"
                           className={`${isDark ? 'text-gray-200' : 'text-gray-700'} uppercase tracking-wide`}>
                           Daybook
                        </Text>
                     </HStack>
                     <FormControl>
                        <div
                           ref={daybookRef}
                           tabIndex={0}
                           onFocus={() => setCurrentFocusIndex(4)}
                           className="focus:outline-none">
                           {renderDynamicDropdown(
                              'daybookId',
                              'Daybook',
                              daybooks.filter((item) =>
                                 isBankPageTrue ? item.type === DAYBOOK_TYPE.BANK : item.type === DAYBOOK_TYPE.CASH,
                              ),
                              {
                                 value: filters.daybookId,
                                 onChange: (value) => handleInputChange('header', 'daybookId', value),
                                 focusBorderColor: 'blue.500',
                                 className: `${isDark
                                    ? 'bg-gray-700 border-gray-600 text-gray-200'
                                    : 'bg-gray-50 border-gray-300 text-gray-700'
                                    } rounded-lg h-11`,
                              },
                              errors,
                              isDark ? '#9CA3AF' : '#6B7280',
                              false,
                              'Select Daybook',
                              true,
                              undefined,
                              true,
                           )}
                        </div>
                     </FormControl>
                  </Box>

                  {/* Account Section */}
                  <Box
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <HStack mb={3} spacing={2}>
                        <Box className="w-1 h-5 bg-pink-500 rounded-full" />
                        <Text
                           fontSize="sm"
                           fontWeight="700"
                           className={`${isDark ? 'text-gray-200' : 'text-gray-700'} uppercase tracking-wide`}>
                           Account
                        </Text>
                     </HStack>
                     <FormControl>
                        <div
                           ref={accountRef}
                           tabIndex={0}
                           onFocus={() => setCurrentFocusIndex(5)}
                           className="focus:outline-none">
                           {/* {renderDynamicDropdown(
                              'accountId',
                              'Party',
                              accounts,
                              {
                                 value: filters.accountId,
                                 onChange: (value) => handleInputChange('header', 'accountId', value),
                                 focusBorderColor: 'blue.500',
                                 className: `${isDark
                                    ? 'bg-gray-700 border-gray-600 text-gray-200'
                                    : 'bg-gray-50 border-gray-300 text-gray-700'
                                    } rounded-lg h-11`,
                              },
                              errors,
                              isDark ? '#9CA3AF' : '#6B7280',
                              false,
                              'Select Party',
                              true,
                              undefined,
                              true,
                           )} */}
                           <MultiSelectDropdown
                              label="Account"
                              options={accounts}
                              value={filters.accountId}
                              onChange={(value) => handleInputChange('header', 'accountId', value)}
                              placeholder="Select Account"
                              isRequired={true}
                              showAddButton={false}
                              onAddNew={(value) => console.log("handleAddNew", value)}
                              isLoading={false}
                              darkMode={!isColorMode}
                              AddEditFormComponent={AddEditForm}
                              error={""}
                           />
                        </div>
                     </FormControl>
                  </Box>
               </VStack>
            </ModalBody>

            {/* Footer */}
            <ModalFooter
               className={`${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'} border-t`}
               gap={3}
               py={4}
               px={6}>
               <Button
                  ref={resetBtnRef}
                  onClick={handleClear}
                  className={`rounded-full font-semibold transition-all duration-200 ${isDark
                     ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600'
                     : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                     }`}
                  flex="1"
                  onFocus={() => setCurrentFocusIndex(6)}
                  h="44px"
                  leftIcon={
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                           d="M4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4"
                           stroke="currentColor"
                           strokeWidth="2"
                           strokeLinecap="round"
                        />
                        <path
                           d="M4 7V12H9"
                           stroke="currentColor"
                           strokeWidth="2"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                        />
                     </svg>
                  }>
                  Reset Filters
               </Button>
               <Button
                  ref={applyBtnRef}
                  onClick={handleApply}
                  className={`flex-1 rounded-xl font-semibold transition-all duration-200 shadow-lg ${isDark
                     ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                     : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                     } text-white`}
                  colorScheme="blue"
                  flex="1"
                  onFocus={() => setCurrentFocusIndex(7)}
                  h="44px"
                  leftIcon={
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                           d="M5 13L9 17L19 7"
                           stroke="currentColor"
                           strokeWidth="2.5"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                        />
                     </svg>
                  }>
                  Apply Filters
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default CashBankFilterModal;
